import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/getErrorMessage";
import {
  ArrowLeft,
  MapPin,
  Mail,
  Phone,
  Clock,
  RefreshCw,
  Truck,
  CheckCircle2,
  XCircle,
  Loader,
  Package,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useOrderDetails } from "../../hooks/useOrderDetails";
import { updateOrderStatus } from "../../services/orderService";

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { order, loading, error } = useOrderDetails(orderId);
  const [currentStatus, setCurrentStatus] = useState(
    order && order.order_status ? order.order_status : "",
  );
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (order && order.order_status && order.order_status !== currentStatus) {
      setCurrentStatus(order.order_status);
    }
  }, [order, currentStatus]);

  const handleStatusChange = async (statusId) => {
    if (!order || updating || statusId === currentStatus) return;
    setUpdating(true);
    try {
      await updateOrderStatus(order.id, { order_status: statusId });
      setCurrentStatus(statusId);
      // Refresh order details after update
      window.location.reload();
    } catch (e) {
      toast.error(getErrorMessage(e, "Failed to update order status."));
    } finally {
      setUpdating(false);
    }
  };

  const statuses = [
    {
      id: "pending_payment",
      icon: Clock,
      color: "text-[#0C0C0E]",
      bgColor: "bg-white",
      borderColor: "border-[#ECEDEF]",
      selectedBg: "bg-[#F7F7F8]",
      selectedBorder: "border-[#F1F2F3]",
    },
    {
      id: "processing",
      icon: RefreshCw,
      color: "text-[#0C0C0E]",
      bgColor: "bg-white",
      borderColor: "border-[#ECEDEF]",
      selectedBg: "bg-[#FFFAEB]",
      selectedBorder: "border-[#FEF0C7]",
    },
    {
      id: "shipped",
      icon: Truck,
      color: "text-[#0C0C0E]",
      bgColor: "bg-white",
      borderColor: "border-[#ECEDEF]",
      selectedBg: "bg-[#EBF5FF]",
      selectedBorder: "border-[#CCE5FF]",
    },
    {
      id: "out_for_delivery",
      icon: Package,
      color: "text-[#0C0C0E]",
      bgColor: "bg-white",
      borderColor: "border-[#ECEDEF]",
      selectedBg: "bg-[#F5F3FF]",
      selectedBorder: "border-[#EDE9FE]",
    },
    {
      id: "delivered",
      icon: CheckCircle2,
      color: "text-[#0C0C0E]",
      bgColor: "bg-white",
      borderColor: "border-[#ECEDEF]",
      selectedBg: "bg-[#EBFAF0]",
      selectedBorder: "border-[#D2F4DB]",
    },
    {
      id: "cancelled",
      icon: XCircle,
      color: "text-[#0C0C0E]",
      bgColor: "bg-white",
      borderColor: "border-[#ECEDEF]",
      selectedBg: "bg-[#FFF5F5]",
      selectedBorder: "border-[#FFDBDC]",
    },
  ];

  if (loading || !order) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <Loader size={32} className="text-[#6B7143] animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#F3F4F6] p-8">
        <div className="text-center">
          <p className="text-red-500">Error loading order: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-4 md:p-8 font-sans text-slate-600">
      {/* --- HEADER --- */}
      <div className="mx-auto mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-800 hover:text-black transition-colors mb-1 active:scale-95 duration-100"
        >
          <ArrowLeft size={20} />
          <h1 className="text-xl font-medium text-[#0C0C0E]">
            Order ORD-{order.id}
          </h1>
        </button>
        <p className="ml-7 font-medium text-sm text-[#7D828D]">
          Placed on {order.created_at ? order.created_at.slice(0, 10) : ""}
        </p>
      </div>

      <div className="mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* --- LEFT COLUMN: ITEMS & STATUS --- */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Items Card */}
          <div className="bg-white rounded-2xl border border-[#F1F2F3] pb-6">
            <h2 className="text-xl p-6 font-medium text-[#0C0C0E] mb-6 border-b border-[#F1F2F3] ">
              Order Items
            </h2>

            <div className="px-6">
              {order.order_items &&
                order.order_items.map((item, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-4 pb-6 ${idx !== order.order_items.length - 1 ? "border-b border-[#ECEDEF] mb-1.5" : ""}`}
                  >
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-[#0C0C0E]">
                            {" "}
                            {item?.product ? item.product.title : "Unknown Product"}
                          </h3>
                          {/* Variations above quantity */}
                          {item.variations && item.variations.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {item.variations.map((variation) => (
                                <span
                                  key={variation.id}
                                  className="inline-block bg-slate-100 text-xs text-slate-700 rounded px-2 py-1 font-medium"
                                >
                                  {variation.name}: {variation.value}
                                </span>
                              ))}
                            </div>
                          )}
                          <p className="text-sm text-[#7D828D] mt-1 font-medium">
                            Quantity: {item.quantity}
                          </p>
                        </div>
                        <span className="text-2xl font-medium text-[#0C0C0E]">
                          ₦{parseInt(item?.unit_price || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

              {/* Price Breakdown */}
              <div className="pt-6 space-y-3">
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-[#7D828D]">Subtotal</span>
                  <span className="text-[#0C0C0E] font-medium">
                    ₦
                    {order.payment_transaction
                      ? parseInt(
                          order.payment_transaction?.amount || 0,
                        ).toLocaleString()
                      : "0"}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-[#7D828D]">Shipping</span>
                  <span className="text-[#0C0C0E] font-medium">
                    ₦
                    {order.order_delivery_info
                      ? parseInt(
                          order.order_delivery_info?.shipping_fee || 0,
                        ).toLocaleString()
                      : "0"}
                  </span>
                </div>
                <div className="flex justify-between pt-4 border-t border-[#ECEDEF] font-medium text-base">
                  <span className="text-[#0C0C0E]">Total</span>
                  <span className="text-[#0C0C0E] font-medium">
                    ₦
                    {order.payment_transaction
                      ? parseInt(
                          order.payment_transaction?.amount || 0,
                        ).toLocaleString()
                      : "0"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Update Status Card - RESTORED ORIGINAL LAYOUT */}
          <div className="bg-white rounded-2xl border border-[#F1F2F3] p-6">
            <h2 className="text-base font-medium text-[#0C0C0E] mb-6">
              Update Status
            </h2>
            <div className="space-y-2">
              {statuses
                .filter((status) => status.id !== "pending_payment")
                .map((status) => {
                  const isSelected = currentStatus === status.id;
                  const Icon = status.icon;
                  return (
                    <button
                      key={status.id}
                      type="button"
                      className={`w-full text-left flex items-center gap-4 p-4 rounded-xl border transition-all ${
                        isSelected
                          ? `${status.selectedBg} ${status.selectedBorder}`
                          : `${status.bgColor} ${status.borderColor} hover:border-[#CCC]`
                      } ${updating ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                      onClick={() => handleStatusChange(status.id)}
                      disabled={updating}
                    >
                      <div
                        className={`p-1 rounded flex items-center justify-center`}
                      >
                        <Icon
                          size={20}
                          className={
                            isSelected ? status.color : "text-slate-300"
                          }
                        />
                      </div>
                      <span
                        className={`text-sm font-bold flex-1 ${isSelected ? status.color : "text-slate-400"}`}
                      >
                        {status.id}
                      </span>
                    </button>
                  );
                })}
            </div>
          </div>
        </div>

        {/* --- RIGHT COLUMN: SHIPPING & CUSTOMER --- */}
        <div className="space-y-6">
          {/* Shipping Address */}
          <div className="bg-white rounded-2xl border border-[#F1F2F3] p-6">
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={18} className="text-[#7D828D]" />
              <h2 className="text-base font-medium text-[#0C0C0E]">
                Shipping Address
              </h2>
            </div>
            <div className="text-sm text-[#7D828D] font-medium space-y-1">
              <p>
                {order.order_delivery_info
                  ? order.order_delivery_info?.address || "N/A"
                  : ""}
              </p>
              <p>
                {order.order_delivery_info
                  ? order.order_delivery_info?.city || ""
                  : ""}
                {order.order_delivery_info && order.order_delivery_info?.state
                  ? `, ${order.order_delivery_info.state}`
                  : ""}
                {order.order_delivery_info && order.order_delivery_info?.region
                  ? `, ${order.order_delivery_info.region}`
                  : ""}
              </p>
              <p>
                {order.order_delivery_info
                  ? order.order_delivery_info?.country || ""
                  : ""}
              </p>
              <p>
                {order.order_delivery_info
                  ? order.order_delivery_info?.phone || ""
                  : ""}
              </p>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-white rounded-2xl border border-[#F1F2F3] p-6">
            <h2 className="text-base font-medium text-[#0C0C0E] mb-5">
              Customer Information
            </h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-[#0C0C0E] mb-3">
                  {order.user ? order.user?.name || "Unknown" : ""}
                </p>
                <div className="flex items-center gap-3 text-sm text-[#7D828D] mb-3 hover:text-[#8B7E74] cursor-pointer">
                  <Mail size={16} />
                  <span className="font-medium text-[#556B2F]">
                    {order.user ? order.user?.email || "N/A" : ""}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm text-[#556B2F] font-medium">
                  <Phone size={16} />
                  <span>{order.user ? order.user?.phone || "N/A" : ""}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
